import { default as React, useRef } from 'react';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { Application } from './Application';
import './AppsByCategory.scss';
import { CategoryWithApps } from './CategoryWithApps';
import { ILocationApplication } from '../../../shared/api/applications/applications';
import { IconButton } from '@fluentui/react';
import { IApplicationOperations } from './Apps';

interface ICategoryProps {
    applicationOperations: IApplicationOperations;
    categoryWithApps: CategoryWithApps;
    isCollapsingAllowed: boolean;
    onToggleCategory: () => void;
    userProfiles: string[];
}

const Category = (props: ICategoryProps) => {
    const nodeRefs = useRef(new Map<string, React.RefObject<HTMLDivElement | null>>());
    const getNodeRef = (id: string) => {
        let nodeRef = nodeRefs.current.get(id);
        if (!nodeRef) {
            nodeRef = React.createRef<HTMLDivElement>();
            nodeRefs.current.set(id, nodeRef);
        }
        return nodeRef;
    };

    return <div key={props.categoryWithApps.id} className="ApplicationGroup ms-slideDownIn10">
        <h1 className={props.isCollapsingAllowed && props.categoryWithApps.isCollapsed ? 'Collapsed' : undefined}>
            {props.isCollapsingAllowed && (
                <IconButton
                    iconProps={{ iconName: 'ChevronUp' }}
                    onClick={props.onToggleCategory}
                    className={props.categoryWithApps.isCollapsed ? 'Collapsed' : undefined}
                />
            )}
            {props.categoryWithApps.name}
        </h1>
        {!(props.isCollapsingAllowed && props.categoryWithApps.isCollapsed) && (
            <TransitionGroup enter={false} exit={true}>
                {props.categoryWithApps.applications.map((a) => {
                    const nodeRef = getNodeRef(a.id);
                    return (
                        <CSSTransition key={`${props.categoryWithApps.id}-${a.id}`} nodeRef={nodeRef} classNames={{ exit: 'ms-slideUpOut10' }} timeout={100}>
                            <div ref={nodeRef}>
                                <Application
                                    userProfiles={props.userProfiles}
                                    application={a}
                                    applicationOperations={props.applicationOperations}
                                    categoryId={props.categoryWithApps.id}
                                    form="Category"
                                />
                            </div>
                        </CSSTransition>
                    );
                })}
            </TransitionGroup>
        )}
    </div>;
};

interface IApplicationCategoryColumnProps {
    applicationOperations: IApplicationOperations;
    categoriesWithApps: CategoryWithApps[];
    isCollapsingAllowed: boolean;
    onToggleCategory: (category: CategoryWithApps) => void;
    userProfiles: string[];
}

const ApplicationCategoryColumn = (props: IApplicationCategoryColumnProps) => {
    const toggleCategory = (category: CategoryWithApps) => () => {
        props.onToggleCategory(category);
    };
    return (
        <div className="ms-slideDownIn10 Column">
            {props.categoriesWithApps.map((c) => (
                <Category
                    key={c.id}
                    categoryWithApps={c}
                    isCollapsingAllowed={props.isCollapsingAllowed}
                    onToggleCategory={toggleCategory(c)}
                    applicationOperations={props.applicationOperations}
                    userProfiles={props.userProfiles}
                />
            ))}
        </div>
    );
};

interface IAppsByCategoryProps {
    applicationOperations: IApplicationOperations;
    applications: ILocationApplication[] | null;
    onToggleCategory: (categoryId: string) => void;
    userProfiles: string[];
}

interface IColumnConfiguration {
    maxDeviation: number;
    columns: CategoryWithApps[][];
}

const getColumnSize = (column: CategoryWithApps[]): number => {
    // headers take up as much space as 2 apps
    return column.map((c) => c.appCount).reduce((a, b) => a + b, 0) + column.length * 2;
};

const getColumnConfiguration = (categories: CategoryWithApps[], colStarts: number[], idealLength: number): IColumnConfiguration => {
    const columns = colStarts.map((start, i) => {
        const end = i < colStarts.length - 1 ? colStarts[i + 1] : categories.length;
        return categories.slice(start, end);
    });

    const maxDeviation = Math.max(...columns.map((col) => getColumnSize(col) - idealLength));

    return {
        maxDeviation,
        columns,
    };
};

const getBestColumnConfiguration = (categories: CategoryWithApps[], columnPermutations: number[][]): CategoryWithApps[][] => {
    const totalAppCount = getColumnSize([...categories]);
    const idealAppCount = totalAppCount / (columnPermutations[0] ? columnPermutations[0].length : 1);

    let bestConfig: IColumnConfiguration = {
        maxDeviation: 99999,
        columns: [categories],
    };

    for (const permutation of columnPermutations) {
        const columnConfig = getColumnConfiguration(categories, permutation, idealAppCount);
        if (columnConfig.maxDeviation < bestConfig.maxDeviation) {
            bestConfig = columnConfig;
        }
    }

    return bestConfig.columns;
};

const getThreeColumnConfiguration = (categories: CategoryWithApps[]): CategoryWithApps[][] => {
    const columnPermutations: number[][] = [];

    for (let col2Start = 1; col2Start < categories.length; col2Start++) {
        for (let col3Start = col2Start + 1; col3Start < categories.length; col3Start++) {
            columnPermutations.push([0, col2Start, col3Start]);
        }
    }

    return getBestColumnConfiguration(categories, columnPermutations);
};

const AppsByCategory: React.FC<IAppsByCategoryProps> = ({ applicationOperations, applications, onToggleCategory, userProfiles }) => {
    const toggleCategory = (categoryWithApps: CategoryWithApps) => {
        onToggleCategory(categoryWithApps.id);
    };

    const getCategoriesWithApps = (): CategoryWithApps[] => {
        if (applications) {
            const categories: { [id: string]: CategoryWithApps } = {};
            applications.map((a) =>
                a.categories.map((c) =>
                    categories[c.id] ? categories[c.id].appendApp(a) : (categories[c.id] = new CategoryWithApps(c)).appendApp(a),
                ),
            );
            return Object.keys(categories)
                .map((id) => categories[id])
                .sort((c1, c2) => c1.name.toUpperCase().localeCompare(c2.name.toUpperCase()) || c1.id.localeCompare(c2.id));
        }
        return [];
    };

    const categoriesWithApps = getCategoriesWithApps();
    const threeColumn = getThreeColumnConfiguration(categoriesWithApps);

    return (
        <div className="ByCategory">
            <div className="OneColumnList">
                <ApplicationCategoryColumn
                    isCollapsingAllowed={true}
                    categoriesWithApps={categoriesWithApps}
                    onToggleCategory={toggleCategory}
                    applicationOperations={applicationOperations}
                    userProfiles={userProfiles}
                />
            </div>
            <div className="ThreeColumnList">
                {threeColumn.map((col, i) => (
                    <ApplicationCategoryColumn
                        isCollapsingAllowed={false}
                        key={i}
                        categoriesWithApps={col}
                        onToggleCategory={toggleCategory}
                        applicationOperations={applicationOperations}
                        userProfiles={userProfiles}
                    />
                ))}
            </div>
        </div>
    );
};

export default AppsByCategory;
